import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/api';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, Lightbulb, TrendingUp, Trash, Bot, Sparkles, Loader2, Copy, Check } from 'lucide-react';

const platforms = ['Facebook', 'Instagram', 'LinkedIn', 'TikTok'];
const contentTypes = ['Post', 'Reel', 'Story'];

const platformColors = {
  'Facebook': 'bg-blue-100 text-blue-800',
  'Instagram': 'bg-pink-100 text-pink-800',
  'LinkedIn': 'bg-indigo-100 text-indigo-800',
  'TikTok': 'bg-gray-900 text-white'
};

const bestPostingTimes = {
  'Facebook': { time: '1-3 PM', days: 'Wed-Fri', engagement: 'High' },
  'Instagram': { time: '11 AM - 1 PM', days: 'Mon-Fri', engagement: 'Very High' },
  'LinkedIn': { time: '8-10 AM', days: 'Tue-Thu', engagement: 'High' },
  'TikTok': { time: '6-9 PM', days: 'Tue-Thu-Fri', engagement: 'Very High' }
};

const contentSuggestions = {
  'Facebook': [
    'Success stories with photos',
    'Educational posts about financial planning',
    'Live Q&A sessions',
    'Team achievements and celebrations'
  ],
  'Instagram': [
    'Behind-the-scenes Stories',
    'Quick tip Reels (15-30 sec)',
    'Client testimonials',
    'Day-in-the-life content'
  ],
  'LinkedIn': [
    'Professional insights',
    'Industry news commentary',
    'Career growth tips',
    'Business opportunity posts'
  ],
  'TikTok': [
    'Quick financial tips (under 60 sec)',
    'Myth-busting videos',
    'Relatable money moments',
    'Success story snippets'
  ]
};

export const SocialMediaCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [contents, setContents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiContent, setAiContent] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [copiedContent, setCopiedContent] = useState(false);
  const [formData, setFormData] = useState({
    content_type: 'Post',
    platform: 'Facebook',
    title: '',
    content_text: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    hashtags: [],
    image_prompt: '',
    status: 'Draft'
  });

  useEffect(() => {
    fetchContents();
    fetchTemplates();
  }, []);

  const fetchContents = async () => {
    try {
      const response = await api.get('/content');
      setContents(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/content/templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    }
  };

  const handleSaveContent = async () => {
    try {
      await api.post('/content', {
        ...formData,
        hashtags: Array.isArray(formData.hashtags) 
          ? formData.hashtags 
          : formData.hashtags.split(' ').filter(Boolean)
      });

      toast.success('Content saved successfully!');
      setShowCreateModal(false);
      fetchContents();
      resetForm();
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      await api.delete(`/content/${contentId}`);
      toast.success('Content deleted successfully!');
      fetchContents();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const resetForm = () => {
    setFormData({
      content_type: 'Post',
      platform: 'Facebook',
      title: '',
      content_text: '',
      scheduled_date: new Date().toISOString().split('T')[0],
      hashtags: [],
      image_prompt: '',
      status: 'Draft'
    });
  };

  // AI Content Generation
  const generateAIContent = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please describe what content you want to create');
      return;
    }

    setAiGenerating(true);
    setAiContent('');

    try {
      const response = await api.post('/ai/chat', {
        message: `Generate a ${formData.content_type} for ${formData.platform} about: ${aiPrompt}

Please provide:
1. A catchy title/hook
2. The main content (optimized for ${formData.platform})
3. 5-7 relevant hashtags
4. Best time to post this content
5. A brief image/visual suggestion

Make it engaging, professional, and suitable for financial services/insurance industry.`,
        mode: 'coaching'
      });

      setAiContent(response.data.response);
      toast.success('Content generated!');
    } catch (error) {
      toast.error('Failed to generate content. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const copyAIContent = () => {
    navigator.clipboard.writeText(aiContent);
    setCopiedContent(true);
    setTimeout(() => setCopiedContent(false), 2000);
    toast.success('Content copied!');
  };

  const useAIContent = () => {
    // Extract content sections from AI response
    const lines = aiContent.split('\n').filter(l => l.trim());
    
    // Try to find title (usually first line or after "Title:")
    let title = '';
    let content = aiContent;
    let hashtags = [];

    lines.forEach(line => {
      if (line.toLowerCase().includes('title:') || line.toLowerCase().includes('hook:')) {
        title = line.replace(/^(title:|hook:)/i, '').trim();
      }
      // Extract hashtags
      const hashtagMatches = line.match(/#\w+/g);
      if (hashtagMatches) {
        hashtags = [...hashtags, ...hashtagMatches];
      }
    });

    setFormData(prev => ({
      ...prev,
      title: title || 'AI Generated Content',
      content_text: aiContent,
      hashtags: hashtags.slice(0, 7)
    }));

    setShowAIModal(false);
    setShowCreateModal(true);
    toast.success('Content added to form!');
  };

  const getContentsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return contents.filter(c => c.scheduled_date === dateStr);
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dayContents = getContentsForDate(date);
      if (dayContents.length > 0) {
        return (
          <div className="flex justify-center mt-1">
            <div className="h-1.5 w-1.5 rounded-full bg-[#E31837]"></div>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="social-media-calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Social Media Content Planner</h1>
          <p className="text-gray-600 mt-1">Plan and organize your daily social media content</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowTemplatesModal(true)}
            className="flex items-center gap-2"
            data-testid="view-roadmap-button"
          >
            <Lightbulb className="h-4 w-4" />
            New Recruit Roadmap
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#E31837] hover:bg-[#C4122C] flex items-center gap-2"
            data-testid="create-content-button"
          >
            <Plus className="h-4 w-4" />
            Add Content Note
          </Button>
        </div>
      </div>

      {/* Best Posting Times & Content Suggestions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-[#E31837]" />
            <h2 className="text-xl font-semibold text-[#333333]">Best Times to Post</h2>
          </div>
          <div className="space-y-4">
            {Object.entries(bestPostingTimes).map(([platform, data]) => (
              <div key={platform} className="border-l-4 border-l-[#E31837] pl-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-[#333333]">{platform}</span>
                  <Badge 
                    variant="outline"
                    className={data.engagement === 'Very High' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}
                  >
                    {data.engagement} Engagement
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Best Time:</span> {data.time}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Best Days:</span> {data.days}
                </p>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-[#E31837]" />
            <h2 className="text-xl font-semibold text-[#333333]">Content Suggestions by Platform</h2>
          </div>
          <Tabs defaultValue="Facebook" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="Facebook">FB</TabsTrigger>
              <TabsTrigger value="Instagram">IG</TabsTrigger>
              <TabsTrigger value="LinkedIn">LI</TabsTrigger>
              <TabsTrigger value="TikTok">TT</TabsTrigger>
            </TabsList>
            {Object.entries(contentSuggestions).map(([platform, suggestions]) => (
              <TabsContent key={platform} value={platform} className="mt-4">
                <ul className="space-y-2">
                  {suggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-[#E31837] mt-1">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </TabsContent>
            ))}
          </Tabs>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <Card className="p-6 lg:col-span-2">
          <h2 className="text-xl font-semibold mb-4 text-[#333333]">Content Calendar</h2>
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            tileContent={tileContent}
            className="w-full border-0 shadow-none"
          />
        </Card>

        {/* Selected Date Content */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-[#333333]">
            {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </h2>
          
          {getContentsForDate(selectedDate).length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No content scheduled for this date</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFormData({ ...formData, scheduled_date: selectedDate.toISOString().split('T')[0] });
                  setShowCreateModal(true);
                }}
                className="mt-3"
              >
                Add Content
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {getContentsForDate(selectedDate).map((content) => (
                <div
                  key={content.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-[#E31837]/50 transition-colors"
                  data-testid={`content-card-${content.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <Badge className={platformColors[content.platform]}>
                      {content.platform}
                    </Badge>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDeleteContent(content.id)}
                        className="p-1 hover:bg-gray-100 rounded"
                        data-testid={`delete-content-${content.id}`}
                      >
                        <Trash className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                  <h4 className="font-semibold text-sm mb-1">{content.title}</h4>
                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">{content.content_text}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{content.content_type}</span>
                    <Badge variant="outline" className="text-xs">
                      {content.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* All Content List */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 text-[#333333]">All Scheduled Content</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contents.length === 0 ? (
            <div className="col-span-full text-center text-gray-500 py-8">
              <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No content planned yet. Start by adding your first note!</p>
            </div>
          ) : (
            contents.map((content) => (
              <div
                key={content.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <Badge className={platformColors[content.platform]}>
                    {content.platform}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    {new Date(content.scheduled_date).toLocaleDateString()}
                  </span>
                </div>
                <h4 className="font-semibold mb-2">{content.title}</h4>
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">{content.content_text}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{content.content_type}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteContent(content.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Create Content Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-content-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#E31837]">Add Content to Calendar</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Content Type</Label>
                <Select
                  value={formData.content_type}
                  onValueChange={(value) => setFormData({ ...formData, content_type: value })}
                >
                  <SelectTrigger data-testid="content-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {contentTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Platform</Label>
                <Select
                  value={formData.platform}
                  onValueChange={(value) => setFormData({ ...formData, platform: value })}
                >
                  <SelectTrigger data-testid="platform-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {platforms.map(platform => (
                      <SelectItem key={platform} value={platform}>{platform}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="title">Title / Topic</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Emergency Fund Tips, Success Story, etc."
                data-testid="title-input"
              />
            </div>

            <div>
              <Label htmlFor="content">Content / Notes</Label>
              <Textarea
                id="content"
                value={formData.content_text}
                onChange={(e) => setFormData({ ...formData, content_text: e.target.value })}
                rows={8}
                placeholder="Write your content ideas, post draft, or notes here..."
                data-testid="content-textarea"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.content_text.length} characters
              </p>
            </div>

            <div>
              <Label htmlFor="hashtags">Hashtags (optional)</Label>
              <Input
                id="hashtags"
                value={Array.isArray(formData.hashtags) ? formData.hashtags.join(' ') : formData.hashtags}
                onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                placeholder="#financialfreedom #insurance #success"
                data-testid="hashtags-input"
              />
            </div>

            <div>
              <Label htmlFor="scheduled_date">Scheduled Date</Label>
              <Input
                id="scheduled_date"
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                data-testid="date-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveContent}
                className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                disabled={!formData.title || !formData.content_text}
                data-testid="save-content-button"
              >
                Save to Calendar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Templates/Roadmap Modal */}
      <Dialog open={showTemplatesModal} onOpenChange={setShowTemplatesModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="templates-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#E31837] flex items-center gap-2">
              <TrendingUp className="h-6 w-6" />
              New Recruit Content Roadmap
            </DialogTitle>
          </DialogHeader>

          {templates && (
            <Tabs defaultValue="roadmap" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="roadmap">30-Day Roadmap</TabsTrigger>
                <TabsTrigger value="topics">Content Topics</TabsTrigger>
              </TabsList>

              <TabsContent value="roadmap" className="space-y-6 mt-4">
                <p className="text-gray-600">
                  Follow this proven roadmap to build credibility and generate leads in your first 30 days.
                </p>
                {Object.entries(templates.new_recruit_roadmap).map(([week, days]) => (
                  <Card key={week} className="p-4">
                    <h3 className="font-bold text-lg mb-3 text-[#E31837]">
                      {week.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h3>
                    <div className="space-y-3">
                      {days.map((day, idx) => (
                        <div key={idx} className="border-l-4 border-l-[#E31837] pl-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm">Day {day.day}</span>
                            <Badge variant="outline" className="text-xs">{day.type}</Badge>
                          </div>
                          <p className="text-sm text-gray-600">{day.description}</p>
                          <p className="text-xs text-gray-500 mt-1">Topic: {day.topic.replace('_', ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="topics" className="space-y-4 mt-4">
                {Object.entries(templates.content_topics).map(([topic, description]) => (
                  <Card key={topic} className="p-4">
                    <h4 className="font-bold text-[#333333] mb-2">
                      {topic.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h4>
                    <p className="text-sm text-gray-600">{description}</p>
                  </Card>
                ))}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
